import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  FaLightbulb,
  FaCircleInfo,
  FaWandMagicSparkles,
  FaCircleCheck,
  FaTriangleExclamation,
  FaCircleXmark,
} from 'react-icons/fa6';
import QuizCheck from './QuizCheck';

// Lesson markdown can include inline Check-Your-Understanding markers like
// `{{check:check_1}}` — we split on those, render the surrounding markdown
// normally, and drop a <QuizCheck> at each marker position. The marker slug
// must match a question's `position_marker` from /api/quizzes/lesson/:id/checks.
//
// Marker matching is case-insensitive: `{{check:Demo_1}}` and `{{check:demo_1}}`
// both match a question stored with marker `demo_1`. Authors should put each
// marker on its own line with a blank line above and below so the surrounding
// markdown blocks (lists, tables, blockquotes) stay structurally intact when
// the body is split into segments.
const CHECK_TOKEN_RE = /\{\{check:([a-z0-9_-]+)\}\}/gi;

function splitOnChecks(md) {
  const parts = [];
  let lastIndex = 0;
  let m;
  CHECK_TOKEN_RE.lastIndex = 0;
  while ((m = CHECK_TOKEN_RE.exec(md)) !== null) {
    if (m.index > lastIndex) {
      parts.push({ type: 'md', text: md.slice(lastIndex, m.index) });
    }
    parts.push({ type: 'check', marker: m[1] });
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < md.length) {
    parts.push({ type: 'md', text: md.slice(lastIndex) });
  }
  return parts;
}

function escapeForRegex(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function extractText(node) {
  if (typeof node === 'string') return node;
  if (Array.isArray(node)) return node.map(extractText).join('');
  if (node && node.props && node.props.children) return extractText(node.props.children);
  return '';
}

const CALLOUT_PATTERNS = [
  { re: /^try this now\b/i, kind: 'try', label: 'Try This Now', Icon: FaWandMagicSparkles },
  { re: /^fun fact\b/i, kind: 'fact', label: 'Fun Fact', Icon: FaLightbulb },
  { re: /^note\b/i, kind: 'note', label: 'Note', Icon: FaCircleInfo },
  { re: /^green light\b/i, kind: 'green', label: 'Green light (allowed)', Icon: FaCircleCheck },
  { re: /^yellow light\b/i, kind: 'yellow', label: 'Yellow light (use carefully)', Icon: FaTriangleExclamation },
  { re: /^red light\b/i, kind: 'red', label: 'Red light (not allowed)', Icon: FaCircleXmark },
];

function detectCallout(children) {
  // children is an array of React nodes; the first <p> typically holds the marker.
  const firstChunk = extractText(children).trim();
  for (const c of CALLOUT_PATTERNS) {
    if (c.re.test(firstChunk)) return c;
  }
  return null;
}

function stripFirstMarker(children, label) {
  // Find the first React element whose extracted text starts with the marker
  // and either drop it (if it was JUST the marker) or replace it with a plain
  // <p> containing the cleaned text. We can't rely on `child.type === 'p'`
  // because the `p` override below makes child.type a function reference.
  const out = [];
  let stripped = false;
  const re = new RegExp(`^${escapeForRegex(label)}[:\\s—–-]*`, 'i');
  React.Children.forEach(children, (child, idx) => {
    if (!stripped && React.isValidElement(child)) {
      const text = extractText(child).trim();
      if (re.test(text)) {
        const cleaned = text.replace(re, '').trim();
        stripped = true;
        if (cleaned) {
          out.push(<p key={`stripped-${idx}`}>{cleaned}</p>);
        }
        return;
      }
    }
    out.push(child);
  });
  return out;
}

const CALLOUT_STYLES = {
  try: 'border-brand-moss/30 bg-brand-sage/15',
  fact: 'border-brand-aubergine/30 bg-brand-aubergine/8',
  note: 'border-brand-deep/25 bg-brand-deep/5',
  green: 'border-emerald-500/35 bg-emerald-50',
  yellow: 'border-amber-400/45 bg-amber-50',
  red: 'border-rose-400/45 bg-rose-50',
};
const CALLOUT_ICON_STYLES = {
  try: 'bg-brand-moss text-bone',
  fact: 'bg-brand-aubergine text-bone',
  note: 'bg-brand-deep text-bone',
  green: 'bg-emerald-600 text-bone',
  yellow: 'bg-amber-600 text-bone',
  red: 'bg-rose-600 text-bone',
};

function isOnlyImageChild(children) {
  // react-markdown wraps standalone images in <p>. Detect that case so we can
  // unwrap the paragraph and avoid <figure> inside <p> (invalid HTML).
  const arr = React.Children.toArray(children).filter(
    (c) => !(typeof c === 'string' && c.trim() === '')
  );
  return arr.length === 1 && React.isValidElement(arr[0]) && arr[0].type === 'img';
}

const components = {
  p({ children }) {
    if (isOnlyImageChild(children)) {
      // Render children directly so the <img> -> <figure> override flows up
      // without being nested inside an invalid <p>.
      return <>{children}</>;
    }
    return <p>{children}</p>;
  },
  h2({ children }) {
    // Headings authored as "## Table N.M: …" render as figure-caption-style
    // text (small, italic, gray, centered) so they sit visually as a table
    // caption rather than a section heading. All other H2s render normally
    // and inherit prose-lesson typography.
    const text = extractText(children).trim();
    if (/^Table\s+\d+\.\d+\s*:/i.test(text)) {
      return (
        <div className="mt-7 mb-2 text-xs text-slate2 italic text-center">
          {children}
        </div>
      );
    }
    return <h2>{children}</h2>;
  },
  blockquote({ children }) {
    const callout = detectCallout(children);
    if (callout) {
      const inner = stripFirstMarker(children, callout.label);
      const Icon = callout.Icon;
      return (
        <aside
          className={`my-7 rounded-2xl border ${CALLOUT_STYLES[callout.kind]} p-5 lg:p-6 not-prose`}
        >
          <div className="flex items-center gap-3 mb-3">
            <span
              className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${CALLOUT_ICON_STYLES[callout.kind]}`}
            >
              <Icon className="text-sm" />
            </span>
            <span className="font-display text-lg text-ink">{callout.label}</span>
          </div>
          <div className="prose-lesson prose-lesson--callout">{inner}</div>
        </aside>
      );
    }
    return (
      <blockquote className="border-l-2 border-brand-deep/40 pl-4 italic text-slate2 my-5">
        {children}
      </blockquote>
    );
  },
  img({ src, alt }) {
    const isFigure = alt && /^figure\s/i.test(alt);
    return (
      <figure className="my-8">
        <img
          src={src}
          alt={alt || ''}
          className="rounded-xl w-full border border-ink/5 shadow-soft"
          loading="lazy"
        />
        {isFigure && (
          <figcaption className="mt-2 text-xs text-slate2 italic text-center">
            {alt}
          </figcaption>
        )}
      </figure>
    );
  },
  table({ children }) {
    return (
      <div className="my-6 overflow-x-auto">
        <table className="w-full text-sm border-collapse">{children}</table>
      </div>
    );
  },
  thead({ children }) {
    return <thead className="bg-cream/70 text-left">{children}</thead>;
  },
  th({ children }) {
    return (
      <th className="px-3 py-2 font-display text-xs uppercase tracking-wider text-brand-deep border-b border-ink/15">
        {children}
      </th>
    );
  },
  td({ children }) {
    return <td className="px-3 py-2 align-top border-b border-ink/8">{children}</td>;
  },
  a({ href, children }) {
    const external = href && /^https?:/i.test(href);
    return (
      <a
        href={href}
        target={external ? '_blank' : undefined}
        rel={external ? 'noopener noreferrer' : undefined}
        className="text-brand-deep underline underline-offset-2 hover:text-ink"
      >
        {children}
      </a>
    );
  },
};

export default function LessonBody({ md, invert = false, checks = [], courseSlug = null }) {
  if (!md) return null;

  // Index inline checks by their position_marker (lowercased so the lookup is
  // case-insensitive — the regex captures with the /i flag, so the captured
  // text can be either case in the source markdown).
  const checksByMarker = new Map();
  checks.forEach((c) => {
    if (c && c.position_marker) {
      checksByMarker.set(String(c.position_marker).toLowerCase(), c);
    }
  });

  const parts = splitOnChecks(md);

  // Fast path: no `{{check:…}}` tokens at all → render the original single block.
  if (parts.length === 1 && parts[0].type === 'md') {
    if (invert) {
      return (
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
          {md}
        </ReactMarkdown>
      );
    }
    return (
      <div className="prose-lesson">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
          {md}
        </ReactMarkdown>
      </div>
    );
  }

  // Mixed path: alternate ReactMarkdown blocks with QuizCheck cards.
  // QuizCheck uses `not-prose` internally so the surrounding `prose-lesson`
  // typography doesn't leak into the question card.
  const rendered = parts.map((p, i) => {
    if (p.type === 'check') {
      const q = checksByMarker.get(p.marker.toLowerCase());
      // Marker present in body but no question seeded for that slug → render
      // nothing (silent), so unseeded markers don't break the page.
      if (!q) return null;
      return <QuizCheck key={`check-${i}-${p.marker}`} question={q} courseSlug={courseSlug} />;
    }
    if (!p.text || !p.text.trim()) return null;
    return (
      <ReactMarkdown
        key={`md-${i}`}
        remarkPlugins={[remarkGfm]}
        components={components}
      >
        {p.text}
      </ReactMarkdown>
    );
  });

  if (invert) return <>{rendered}</>;
  return <div className="prose-lesson">{rendered}</div>;
}
